import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useParams } from "react-router-dom";

import {
  getEmployeeAddressInfo,
  updateEmployeeAddressInfo,
} from "app/services/Services";

import {
  generateErrorMessage,
  generateSuccessMessage,
} from "app/utils/GenerateMessage";
import {
  T,
  sectionCardSx,
  muiFieldSx,
  SectionHeader,
  StatusChip,
  PrimaryButton,
  SecondaryButton,
  LoadingBlock,
  ErrorBlock,
} from "./shared/formKit";

const validationSchema = Yup.object().shape({
  current_address: Yup.object().shape({
    addressLine1: Yup.string(),
    addressLine2: Yup.string(),
    city: Yup.string(),
    state: Yup.string(),
    pincode: Yup.string().matches(/^[0-9]{6}$/, "Pincode must be 6 digits"),
    country: Yup.string(),
  }),
  permanent_address: Yup.object().shape({
    addressLine1: Yup.string(),
    addressLine2: Yup.string(),
    city: Yup.string(),
    state: Yup.string(),
    pincode: Yup.string().matches(/^[0-9]{6}$/, "Pincode must be 6 digits"),
    country: Yup.string(),
  }),
  emergency_contact: Yup.object().shape({
    personName: Yup.string(),
    personRelation: Yup.string(),
    personMobile: Yup.string().matches(/^[0-9]{10}$/, "Mobile must be 10 digits"),
    personEmail: Yup.string().email("Valid email is required"),
    personAddress: Yup.string(),
  }),
  both_address_same: Yup.boolean(),
});

const relationships = ["Father", "Mother", "Spouse", "Sibling", "Child", "Friend", "Other"];

const EmployeeAddressContact = ({ onCompleted = null }) => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [existingData, setExistingData] = useState(null);
  const [employeeId] = useState(id);

  const isEdit = !!existingData;

  useEffect(() => {
    loadExistingData();
  }, [id]);

  const loadExistingData = async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const response = await getEmployeeAddressInfo(id);
      if (response?.status === 200) {
        const data = response.data?.data || null;
        setExistingData(data);
      } else {
        setExistingData(null);
      }
    } catch (err) {
      console.error(err);
      setExistingData(null);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const getInitialValues = () => {
    const blankAddress = { addressLine1: "", addressLine2: "", city: "", state: "", pincode: "", country: "India" };
    if (isEdit && existingData) {
      return {
        current_address: existingData.current_address || blankAddress,
        permanent_address: existingData.permanent_address || blankAddress,
        emergency_contact: existingData.emergency_contact || {
          personName: "", personRelation: "", personMobile: "", personEmail: "", personAddress: "",
        },
        both_address_same: existingData.both_address_same || false,
      };
    }
    return {
      current_address: blankAddress,
      permanent_address: { ...blankAddress },
      emergency_contact: { personName: "", personRelation: "", personMobile: "", personEmail: "", personAddress: "" },
      both_address_same: false,
    };
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitting(true);

      const submitData = {
        currentAddress: values.current_address,
        permanentAddress: values.both_address_same ? values.current_address : values.permanent_address,
        emergencyContact: values.emergency_contact,
        bothAddressSame: values.both_address_same,
      };

      const response = await updateEmployeeAddressInfo(employeeId, submitData);

      if (response?.status === 200) {
        generateSuccessMessage("Employee address information saved successfully");
        await loadExistingData();
        if (onCompleted) onCompleted(values);
      }
    } catch (err) {
      console.error("Submit error:", err);
      generateErrorMessage(err.response?.data?.message || "Failed to save employee address information");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={sectionCardSx}>
        <LoadingBlock label="Loading address data…" />
      </Box>
    );
  }

  return (
    <Box>
      {loadError && <ErrorBlock message="Failed to load address data." onRetry={loadExistingData} />}

      <Formik
        enableReinitialize
        initialValues={getInitialValues()}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting }) => (
          <Form>
            {/* Current address */}
            <Box sx={sectionCardSx}>
              <SectionHeader title="Current address" action={<StatusChip label={isEdit ? "Editing" : "New"} tone="brand" />} />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth size="small" sx={muiFieldSx}
                    name="current_address.addressLine1" label="Address line 1"
                    value={values.current_address.addressLine1} onChange={handleChange} onBlur={handleBlur}
                    error={touched.current_address?.addressLine1 && Boolean(errors.current_address?.addressLine1)}
                    helperText={touched.current_address?.addressLine1 && errors.current_address?.addressLine1}
                    disabled={isSubmitting}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth size="small" sx={muiFieldSx}
                    name="current_address.addressLine2" label="Address line 2"
                    value={values.current_address.addressLine2} onChange={handleChange} onBlur={handleBlur}
                    error={touched.current_address?.addressLine2 && Boolean(errors.current_address?.addressLine2)}
                    helperText={touched.current_address?.addressLine2 && errors.current_address?.addressLine2}
                    disabled={isSubmitting}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth size="small" sx={muiFieldSx}
                    name="current_address.city" label="City"
                    value={values.current_address.city} onChange={handleChange} onBlur={handleBlur}
                    error={touched.current_address?.city && Boolean(errors.current_address?.city)}
                    helperText={touched.current_address?.city && errors.current_address?.city}
                    disabled={isSubmitting}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth size="small" sx={muiFieldSx}
                    name="current_address.state" label="State"
                    value={values.current_address.state} onChange={handleChange} onBlur={handleBlur}
                    error={touched.current_address?.state && Boolean(errors.current_address?.state)}
                    helperText={touched.current_address?.state && errors.current_address?.state}
                    disabled={isSubmitting}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth size="small" sx={muiFieldSx}
                    name="current_address.pincode" label="Pincode"
                    value={values.current_address.pincode} onChange={handleChange} onBlur={handleBlur}
                    error={touched.current_address?.pincode && Boolean(errors.current_address?.pincode)}
                    helperText={touched.current_address?.pincode && errors.current_address?.pincode}
                    disabled={isSubmitting}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth size="small" sx={muiFieldSx}
                    name="current_address.country" label="Country"
                    value={values.current_address.country} onChange={handleChange} onBlur={handleBlur}
                    error={touched.current_address?.country && Boolean(errors.current_address?.country)}
                    helperText={touched.current_address?.country && errors.current_address?.country}
                    disabled={isSubmitting}
                  />
                </Grid>
              </Grid>

              <Box mt={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.both_address_same}
                      onChange={(e) => {
                        setFieldValue("both_address_same", e.target.checked);
                        if (e.target.checked) {
                          setFieldValue("permanent_address", values.current_address);
                        }
                      }}
                      disabled={isSubmitting}
                      sx={{ color: T.cardBorder, "&.Mui-checked": { color: "#e059f0" } }}
                    />
                  }
                  label={<Box sx={{ fontSize: "13.5px", color: T.body }}>Permanent address is same as current address</Box>}
                />
              </Box>
            </Box>

            {/* Permanent address */}
            {!values.both_address_same && (
              <Box sx={sectionCardSx}>
                <SectionHeader title="Permanent address" />
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth size="small" sx={muiFieldSx}
                      name="permanent_address.addressLine1" label="Address line 1"
                      value={values.permanent_address.addressLine1} onChange={handleChange} onBlur={handleBlur}
                      error={touched.permanent_address?.addressLine1 && Boolean(errors.permanent_address?.addressLine1)}
                      helperText={touched.permanent_address?.addressLine1 && errors.permanent_address?.addressLine1}
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth size="small" sx={muiFieldSx}
                      name="permanent_address.addressLine2" label="Address line 2"
                      value={values.permanent_address.addressLine2} onChange={handleChange} onBlur={handleBlur}
                      error={touched.permanent_address?.addressLine2 && Boolean(errors.permanent_address?.addressLine2)}
                      helperText={touched.permanent_address?.addressLine2 && errors.permanent_address?.addressLine2}
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth size="small" sx={muiFieldSx}
                      name="permanent_address.city" label="City"
                      value={values.permanent_address.city} onChange={handleChange} onBlur={handleBlur}
                      error={touched.permanent_address?.city && Boolean(errors.permanent_address?.city)}
                      helperText={touched.permanent_address?.city && errors.permanent_address?.city}
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth size="small" sx={muiFieldSx}
                      name="permanent_address.state" label="State"
                      value={values.permanent_address.state} onChange={handleChange} onBlur={handleBlur}
                      error={touched.permanent_address?.state && Boolean(errors.permanent_address?.state)}
                      helperText={touched.permanent_address?.state && errors.permanent_address?.state}
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth size="small" sx={muiFieldSx}
                      name="permanent_address.pincode" label="Pincode"
                      value={values.permanent_address.pincode} onChange={handleChange} onBlur={handleBlur}
                      error={touched.permanent_address?.pincode && Boolean(errors.permanent_address?.pincode)}
                      helperText={touched.permanent_address?.pincode && errors.permanent_address?.pincode}
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth size="small" sx={muiFieldSx}
                      name="permanent_address.country" label="Country"
                      value={values.permanent_address.country} onChange={handleChange} onBlur={handleBlur}
                      error={touched.permanent_address?.country && Boolean(errors.permanent_address?.country)}
                      helperText={touched.permanent_address?.country && errors.permanent_address?.country}
                      disabled={isSubmitting}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Emergency contact */}
            <Box sx={sectionCardSx}>
              <SectionHeader title="Emergency contact" />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth size="small" sx={muiFieldSx}
                    name="emergency_contact.personName" label="Contact name"
                    value={values.emergency_contact.personName} onChange={handleChange} onBlur={handleBlur}
                    error={touched.emergency_contact?.personName && Boolean(errors.emergency_contact?.personName)}
                    helperText={touched.emergency_contact?.personName && errors.emergency_contact?.personName}
                    disabled={isSubmitting}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small" sx={muiFieldSx} disabled={isSubmitting}>
                    <InputLabel>Relationship</InputLabel>
                    <Select
                      name="emergency_contact.personRelation"
                      value={values.emergency_contact.personRelation}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="Relationship"
                      error={touched.emergency_contact?.personRelation && Boolean(errors.emergency_contact?.personRelation)}
                    >
                      {relationships.map((rel) => (
                        <MenuItem key={rel} value={rel}>{rel}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth size="small" sx={muiFieldSx}
                    name="emergency_contact.personMobile" label="Mobile number"
                    value={values.emergency_contact.personMobile} onChange={handleChange} onBlur={handleBlur}
                    error={touched.emergency_contact?.personMobile && Boolean(errors.emergency_contact?.personMobile)}
                    helperText={touched.emergency_contact?.personMobile && errors.emergency_contact?.personMobile}
                    disabled={isSubmitting}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth size="small" sx={muiFieldSx}
                    type="email" name="emergency_contact.personEmail" label="Email"
                    value={values.emergency_contact.personEmail} onChange={handleChange} onBlur={handleBlur}
                    error={touched.emergency_contact?.personEmail && Boolean(errors.emergency_contact?.personEmail)}
                    helperText={touched.emergency_contact?.personEmail && errors.emergency_contact?.personEmail}
                    disabled={isSubmitting}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth size="small" sx={muiFieldSx}
                    name="emergency_contact.personAddress" label="Address" multiline rows={2}
                    value={values.emergency_contact.personAddress} onChange={handleChange} onBlur={handleBlur}
                    error={touched.emergency_contact?.personAddress && Boolean(errors.emergency_contact?.personAddress)}
                    helperText={touched.emergency_contact?.personAddress && errors.emergency_contact?.personAddress}
                    disabled={isSubmitting}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box display="flex" gap={2}>
              <PrimaryButton disabled={isSubmitting}>
                {isSubmitting ? "Saving…" : isEdit ? "Update details" : "Save details"}
              </PrimaryButton>
              {isEdit && (
                <SecondaryButton type="button" onClick={loadExistingData} disabled={isSubmitting}>
                  Refresh
                </SecondaryButton>
              )}
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default EmployeeAddressContact;
